import React, { useState, useEffect, useRef } from 'react';
import { Group, Rect, Text, Transformer } from 'react-konva';

export interface MindNodeProps {
  id: string;
  text: string;
  x: number;
  y: number;
  color: string;
  fontSize: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  isSelected: boolean;
  onMove: (id: string, x: number, y: number) => void;
  onSelect: (id: string) => void;
  stageScale: number;
  width: number;
  height: number;
  onResize?: (id:string,width:number,height:number)=>void;
}

const KonvaMindNode: React.FC<MindNodeProps> = ({ id, text, x, y, color, fontSize, bold, italic, underline, isSelected, onMove, onSelect, stageScale, width, height, onResize }) => {
  const [currentText, setCurrentText] = useState(text);
  const [isEditing, setIsEditing] = useState(false);
  const groupRef = useRef<any>(null);
  const trRef = useRef<any>(null);

  useEffect(() => { setCurrentText(text); }, [text]);

  // textarea overlay for editing, adapted from KonvaStickyNote
  useEffect(() => {
    if (isEditing) {
      const textarea = document.createElement('textarea');
      textarea.value = currentText;
      textarea.style.position = 'absolute';

      // calculate absolute page position and size so textarea aligns with the node even when zoomed / panned
      const stage = groupRef.current.getStage();
      const containerRect = stage.container().getBoundingClientRect();
      const absPos = groupRef.current.getAbsolutePosition();
      const pad = 8 * stageScale;

      textarea.style.top = `${containerRect.top + absPos.y + pad}px`;
      textarea.style.left = `${containerRect.left + absPos.x + pad}px`;
      textarea.style.width = `${width * stageScale - 2 * pad}px`;
      textarea.style.height = `${height * stageScale - 2 * pad}px`;
      textarea.style.fontSize = `${fontSize / stageScale}px`;
      textarea.style.background = 'transparent';
      textarea.style.border = 'none';
      textarea.style.color = '#111827';
      textarea.style.padding = '0';
      textarea.style.boxSizing = 'border-box';
      textarea.style.outline = 'none';
      textarea.style.resize = 'none';

      document.body.appendChild(textarea);
      textarea.focus();

      // live update while typing
      textarea.oninput = () => {
        setCurrentText(textarea.value);
        // auto-grow height if content exceeds current box
        const contentH = textarea.scrollHeight;
        const desiredH = contentH / stageScale + (2 * pad) / stageScale;
        if (desiredH > height || desiredH < height - 24) {
          onResize && onResize(id, width, desiredH);
          const rectNode = groupRef.current.findOne('Rect');
          if (rectNode) rectNode.height(desiredH);
          const textNode = groupRef.current.findOne('Text');
          if (textNode) textNode.height(desiredH);
        }
      };

      textarea.onblur = () => {
        setIsEditing(false);
        setCurrentText(textarea.value);
        if (document.body.contains(textarea)) {
          document.body.removeChild(textarea);
        }
      };
      // cleanup on effect disposal
      return () => {
        if (document.body.contains(textarea)) {
          document.body.removeChild(textarea);
        }
      };
    }
  }, [isEditing, fontSize]);

  useEffect(()=>{
    if(isSelected && trRef.current){ trRef.current.nodes([groupRef.current]); trRef.current.getLayer().batchDraw(); }
  },[isSelected]);

  const handleTransformEnd=()=>{
    const node=groupRef.current;
    const rect=node.findOne('Rect');
    const newW=Math.max(80, rect.width()*node.scaleX());
    const newH=Math.max(40, rect.height()*node.scaleY());
    rect.width(newW);
    rect.height(newH);
    const text=node.findOne('Text');
    text.width(newW);
    text.height(newH);
    node.scale({x:1,y:1});
    onResize && onResize(id,newW,newH);
  };

  return (
    <>
    <Group
      x={x}
      y={y}
      draggable
      onDragEnd={(e) => onMove(id, e.target.x(), e.target.y())}
      onClick={() => onSelect(id)}
      onDblClick={() => setIsEditing(true)}
      ref={groupRef}
      onTransformEnd={handleTransformEnd}
    >
      <Rect
        width={width}
        height={height}
        fill={color}
        cornerRadius={8}
        stroke={isSelected ? '#3b82f6' : undefined}
        strokeWidth={isSelected ? 2 : 0}
        shadowBlur={4}
      />
      <Text
        visible={!isEditing}
        text={currentText}
        width={width}
        height={height}
        align="center"
        verticalAlign="middle"
        fontSize={fontSize}
        fontStyle={`${bold ? 'bold' : ''} ${italic ? 'italic' : ''}`.trim()}
        textDecoration={underline ? 'underline' : undefined}
        fill="#111827"
        padding={8}
      />
    </Group>
    {isSelected && <Transformer ref={trRef} boundBoxFunc={(o,n)=>{ if(n.width<80||n.height<40) return o; return n; }} />}
    </>
  );
};

export default KonvaMindNode; 